import { default as React, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Col,
  Row,
  Nav,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PacmanLoader } from "react-spinners";
import { LinkContainer } from "react-router-bootstrap";
import "containers/common/List.css";
import "containers/common/Table.css";

import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import { PAGE_SIZES } from "utils/pageSizeConfig";

function SLMAddressList({ isAuthenticated, data, search_count }) {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [, setAddresses] = useState([]);


  // --- Data Fetching and Update Functions ---

  useEffect(() => {
    console.log("In AddressList componentDidMount");
    if (!isAuthenticated) {
      return;
    }
    setIsLoading(false);
  }, [isAuthenticated]);


  const handleInputChange = (cellInfo, event) => {
    let localdata = [...data];
    localdata[cellInfo.row.index][cellInfo.column.id] = event.target.value;
    localdata[cellInfo.row.index].modified = true;
  };


  const handleNumberChange = (cellInfo, event) => {
    let localdata = [...data];
    let val = event.target.value.replace(/[^0-9]/g, "");
    localdata[cellInfo.row.index][cellInfo.column.id] = val;
    localdata[cellInfo.row.index].modified = true;
  };


  const handleCheckboxChange = (cellInfo, event) => {
    let localdata = [...data];
    localdata[cellInfo.row.index][cellInfo.column.id] = event.target.checked;
    localdata[cellInfo.row.index].modified = true;
    setAddresses(localdata);
    handleSave(cellInfo, event);
  };


  const handleDateChange = (cellInfo, event) => {
    let localaddresses = [...data];
    localaddresses[cellInfo.row.index][cellInfo.column.id] = new Date(event);
    localaddresses[cellInfo.row.index].modified = true;
    setAddresses(localaddresses);
    handleSave(cellInfo, event);
  };

  const handleSave = () => {
    // Save behavior is wired in legacy flows; keep this as a safe no-op for now.
  };

  const handleBlur = () => {
    // Blur currently marks edited values through local state updates.
  };


  const renderEditable = (cellInfo) => {
    const cellValue = data[cellInfo.row.index][cellInfo.column.id];

    switch (typeof cellValue) {
      case "boolean":
        return (
          <div className="d-flex justify-content-center align-items-center w-100">
            <input
              className="form-check-input position-static m-0"
              checked={cellValue}
              onChange={handleCheckboxChange.bind(cellValue != null ? cellValue : "", cellInfo)}
              type="checkbox"
            />
          </div>
        );

      case "number":
        return (
          <input
            onChange={handleNumberChange.bind(cellValue != null ? cellValue : "", cellInfo)}
            onBlur={handleBlur.bind(cellValue != null ? cellValue : "", cellInfo)}
            type="number"
            className="no-border form-control"
            defaultValue={cellValue != null ? cellValue : " "}
          />
        );

      default:
        if (cellInfo.column.id.indexOf("date") >= 0) {
          return (
            <DatePicker
              selected={cellValue != null ? new Date(cellValue) : ""}
              onChange={handleDateChange.bind(cellValue != null ? cellValue : "", cellInfo)}
              onBlur={handleBlur.bind(cellValue != null ? cellValue : "", cellInfo)}
              dateFormat="dd/MM/yyyy"
              className="no-border form-control"
            />
          );
        } else {
          return (
            <input
              onChange={handleInputChange.bind(cellValue != null ? cellValue : "", cellInfo)}
              onBlur={handleBlur.bind(cellValue != null ? cellValue : "", cellInfo)}
              type="text"
              className="no-border form-control"
              defaultValue={cellValue != null ? cellValue : ""}
            />
          );
        }
    }
  };



  // --- TanStack Table Columns ---

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("selected", {
      id: "selected",
      header: "",
      cell: renderEditable,
      size: 60,
      enableSorting: false,
    }),
    columnHelper.accessor("address_id", {
      id: "address_id",
      header: "CoLin ID",
      cell: (info) => {
        const clickedId = info.getValue();
        const selectedIds = (data || []).filter((a) => a.selected).map((a) => a.address_id);
        const browseIds = selectedIds.length > 0 ? selectedIds : [clickedId];

        return (
          <a
            href={`/slmaddresses/${clickedId}`}
            onClick={(event) => {
              event.preventDefault();
              navigate(`/slmaddresses/${clickedId}`, { state: { data: browseIds } });
            }}
          >
            {clickedId}
          </a>
        );
      },
      size: 80,
    }),
    columnHelper.accessor("hard_sequence", {
      id: "hard_sequence",
      header: "PIMs ID",
      cell: (info) => info.getValue(),
      size: 80,
    }),
    columnHelper.accessor("building_name", {
      id: "building_name",
      header: "Building Name",
      cell: (info) => info.getValue(),
      size: 140,
    }),
    columnHelper.accessor((d) => d.street_address, {
      id: "street_address",
      header: "Street Address",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("suburb", {
      id: "suburb",
      header: "Suburb",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("state_code", {
      id: "state_code",
      header: "State",
      cell: (info) => info.getValue(),
      size: 70,
    }),
    columnHelper.accessor("postcode", {
      id: "postcode",
      header: "Postcode",
      cell: (info) => info.getValue(),
      size: 80,
    }),
    columnHelper.accessor("cityscope_reference", {
      id: "cityscope_reference",
      header: "Cityscope Ref.",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("lot_plan", {
      id: "lot_plan",
      header: "Lot Plan",
      cell: (info) => info.getValue(),
      size: 90,
    }),
    columnHelper.accessor("title_reference", {
      id: "title_reference",
      header: "Title Ref.",
      cell: (info) => info.getValue(),
      size: 90,
    }),
    columnHelper.accessor("sold_count", {
      id: "sold_count",
      header: "Sold",
      cell: (info) => info.getValue(),
      size: 60,
    }),
    columnHelper.accessor("sale_count", {
      id: "sale_count",
      header: "For Sale",
      cell: (info) => info.getValue(),
      size: 70,
    }),
    columnHelper.accessor("leased_count", {
      id: "leased_count",
      header: "Leased",
      cell: (info) => info.getValue(),
      size: 70,
    }),
    columnHelper.accessor("lease_count", {
      id: "lease_count",
      header: "For Lease",
      cell: (info) => info.getValue(),
      size: 80,
    }),
  ];


  const selectAll = (event) => {
    let localaddresses = [...data];
    for (var i = 0; i < localaddresses.length; i++) {
      localaddresses[i].selected = true;
    }
    setAddresses(localaddresses);
  };


  const deselectAll = (event) => {
    let localaddresses = [...data];
    for (var i = 0; i < localaddresses.length; i++) {
      localaddresses[i].selected = false;
    }
    setAddresses(localaddresses);
  };


  function renderAddresses() {
    return (
      <div className="addresses">
        <Row>
          <Col className="text-left">
            <Nav className="justify-content-start">
              <Nav.Item>
                <Button
                  variant="primary"
                  type="button"
                  size="sm"
                  onClick={selectAll}
                >
                  Select All
                </Button>{" "}
              </Nav.Item>{" "}
              <Nav.Item>
                <Button
                  variant="primary"
                  type="button"
                  size="sm"
                  onClick={deselectAll}
                >
                  Deselect All
                </Button>{" "}
              </Nav.Item>
            </Nav>
          </Col>

          <Col className="text-right" sm={4} md={3} lg={2}>
            {search_count > 0 && (
              <LinkContainer to={`/slmaddresses/new`}>
                <Button variant="primary" type="button" size="sm">
                  Add New
                </Button>
              </LinkContainer>
            )}
          </Col>
        </Row>

        <Row>
          <Col>
            <b>
              {data ? data.length : "0"} of{" "}
              {data ? (data.length > 0 ? data[0].result_count : "0") : "0"}{" "}
              records returned.
            </b>
          </Col>
        </Row>

        <Row>
          <Col sm={12} md={12} lg={12}>
            {isLoading && (
              <div className="outer-div">
                <div className="sweet-loading inner-div">
                  <PacmanLoader
                    cssOverride={{ display: "block", margin: "0 auto" }}
                    size={20}
                    color={"#e03c31"}
                    loading={isLoading}
                  />
                </div>
              </div>
            )}
            {!isLoading && search_count === 0 && (
              <div className="alert alert-secondary">Begin by entering search criteria or just clicking 'Search'</div>
            )}
            {!isLoading && search_count > 0 && (
              <GenericTable
                columns={columns}
                data={data || []}
                pageSize={PAGE_SIZES.slmAddressSearch}
                enablePagination={true}
                hasHeaderSorting={true}
                section="slm-address-search"
              />
            )}
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="AddressList">
      {/* {renderComponent()} */}
      {isAuthenticated ? renderAddresses() : console.log("error)")}
    </div>
  );
}

export default SLMAddressList;
